import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Tab,
    Tabs,
    InputAdornment,
    Link as MuiLink,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createEditor } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { resolveSupportAssetUrl } from '../utils/supportAssets';
import SupportTicketWorkspace from './SupportTicketWorkspace';

const EMPTY_CONTENT = [
    {
        type: 'paragraph',
        children: [{ text: '' }],
    },
];

const safeDoc = (val) =>
    Array.isArray(val) && val.length ? val : EMPTY_CONTENT;

const formatDate = (value) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).format(date);
    } catch (error) {
        return '';
    }
};

const SupportArticleViewer = ({ content }) => {
    const editor = useMemo(() => withReact(createEditor()), []);
    const doc = useMemo(() => safeDoc(content), [content]);

    // Re-mount Slate when incoming content changes so it re-reads initialValue.
    const [slateKey, setSlateKey] = useState(0);
    useEffect(() => {
        setSlateKey((k) => k + 1);
    }, [doc]);

    const renderElement = useCallback((props) => {
        const { element, attributes, children } = props;
        switch (element.type) {
            case 'heading-one':
                return (
                    <h1 {...attributes} style={{ fontSize: '1.9rem', margin: '24px 0 12px' }}>
                        {children}
                    </h1>
                );
            case 'heading-two':
                return (
                    <h2 {...attributes} style={{ fontSize: '1.5rem', margin: '20px 0 10px' }}>
                        {children}
                    </h2>
                );
            case 'block-quote':
                return (
                    <blockquote
                        {...attributes}
                        style={{
                            borderLeft: '4px solid #90caf9',
                            margin: '20px 0',
                            padding: '8px 20px',
                            color: '#455a64',
                            fontStyle: 'italic',
                            background: '#f1f8ff',
                        }}
                    >
                        {children}
                    </blockquote>
                );
            case 'bulleted-list':
                return (
                    <ul {...attributes} style={{ margin: '12px 0 12px 28px' }}>
                        {children}
                    </ul>
                );
            case 'numbered-list':
                return (
                    <ol {...attributes} style={{ margin: '12px 0 12px 28px' }}>
                        {children}
                    </ol>
                );
            case 'list-item':
                return <li {...attributes}>{children}</li>;
            case 'code':
                return (
                    <pre
                        {...attributes}
                        style={{
                            background: '#263238',
                            color: '#fff',
                            padding: '16px',
                            borderRadius: 8,
                            overflowX: 'auto',
                        }}
                    >
                        <code>{children}</code>
                    </pre>
                );
            case 'image':
                return (
                    <div {...attributes} style={{ textAlign: 'center', margin: '20px 0' }}>
                        <div contentEditable={false}>
                            <img
                                src={resolveSupportAssetUrl(element.url)}
                                alt={element.alt || 'Support visual'}
                                style={{ maxWidth: '100%', borderRadius: 12 }}
                            />
                        </div>
                        {children}
                    </div>
                );
            default:
                return (
                    <p
                        {...attributes}
                        style={{ margin: '12px 0', lineHeight: 1.7, color: '#263238', fontSize: '1rem' }}
                    >
                        {children}
                    </p>
                );
        }
    }, []);

    const renderLeaf = useCallback((props) => {
        const { attributes, children, leaf } = props;
        let style = {};
        if (leaf.bold) {
            style.fontWeight = 'bold';
        }
        if (leaf.italic) {
            style.fontStyle = 'italic';
        }
        if (leaf.underline) {
            style.textDecoration = style.textDecoration ? `${style.textDecoration} underline` : 'underline';
        }
        if (leaf.code) {
            style = {
                ...style,
                background: '#eceff1',
                borderRadius: 4,
                fontFamily: 'monospace',
                padding: '0 4px',
            };
        }
        return (
            <span {...attributes} style={style}>
                {children}
            </span>
        );
    }, []);

    return (
        <Slate key={slateKey} editor={editor} initialValue={doc}>
            <Editable readOnly renderElement={renderElement} renderLeaf={renderLeaf} spellCheck={false} />
        </Slate>
    );
};

const SupportCenter = () => {
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState('');
    const [expandedCategory, setExpandedCategory] = useState('');

    const [article, setArticle] = useState(null);
    const [articleLoading, setArticleLoading] = useState(false);
    const [articleError, setArticleError] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    const navigate = useNavigate();
    const { articleSlug } = useParams();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('knowledge');
    const [initialTicketId, setInitialTicketId] = useState('');

    const fetchCategories = useCallback(async () => {
        setCategoriesLoading(true);
        setCategoriesError('');
        try {
            const response = await axios.get('/api/support/categories');
            const list = (response.data || []).map((category) => {
                const articles = (category.articles || []).map((article) => ({
                    ...article,
                    categoryId: category.id,
                    categoryName: category.name,
                }));
                const featuredArticleWithImage = articles.find((article) => Boolean(article.heroImageUrl));
                const categoryImage = category.featuredImageUrl || '';
                return {
                    ...category,
                    articles,
                    featuredImageUrl: categoryImage || featuredArticleWithImage?.heroImageUrl || '',
                };
            });
            setCategories(list);
            setExpandedCategory((prev) => {
                if (prev) return prev;
                const firstCategory = list.find((category) => category.articles && category.articles.length);
                return firstCategory ? firstCategory.id : list[0]?.id || '';
            });
        } catch (error) {
            setCategoriesError('Unable to load support content. Please try again later.');
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const fetchArticle = useCallback(
        async (slug) => {
            if (!slug) {
                setArticle(null);
                return;
            }
            setArticleLoading(true);
            setArticleError('');
            try {
                const response = await axios.get(`/api/support/articles/${slug}`);
                setArticle(response.data);
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    setArticleError('This article could not be found or is no longer published.');
                } else {
                    setArticleError('Failed to load the article. Please try again later.');
                }
                setArticle(null);
            } finally {
                setArticleLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        fetchArticle(articleSlug);
    }, [articleSlug, fetchArticle]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search || '');
        const ticketId = searchParams.get('ticket');
        const tabValue = searchParams.get('tab');
        setInitialTicketId((prev) => {
            const normalized = ticketId || '';
            return prev === normalized ? prev : normalized;
        });
        setActiveTab((prev) => {
            if (ticketId || tabValue === 'tickets') {
                return prev === 'tickets' ? prev : 'tickets';
            }
            return prev === 'knowledge' ? prev : 'knowledge';
        });
    }, [location.search]);

    const searchResults = useMemo(() => {
        if (!searchTerm.trim()) {
            return [];
        }
        const term = searchTerm.trim().toLowerCase();
        const results = [];
        categories.forEach((category) => {
            (category.articles || []).forEach((articleSummary) => {
                if (
                    articleSummary.title.toLowerCase().includes(term) ||
                    (articleSummary.summary && articleSummary.summary.toLowerCase().includes(term))
                ) {
                    results.push({
                        ...articleSummary,
                        category,
                    });
                }
            });
        });
        return results;
    }, [categories, searchTerm]);

    const handleArticleSelect = (selectedArticle) => {
        if (!selectedArticle) return;
        setArticleError('');
        setActiveTab('knowledge');
        const categoryId = selectedArticle.categoryId || selectedArticle.category?.id || '';
        if (categoryId) {
            setExpandedCategory(categoryId);
        }
        navigate(`/dashboard/support/${selectedArticle.slug}`);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchCategories();
        if (articleSlug) {
            await fetchArticle(articleSlug);
        }
        setRefreshing(false);
    };

    const latestArticles = useMemo(() => {
        const flattened = [];
        categories.forEach((category) => {
            (category.articles || []).forEach((articleSummary) => {
                flattened.push({
                    ...articleSummary,
                    category: {
                        id: category.id,
                        name: category.name,
                    },
                });
            });
        });
        return flattened
            .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
            .slice(0, 3);
    }, [categories]);

    const handleTabChange = useCallback(
        (_event, value) => {
            if (value === activeTab) return;
            setActiveTab(value);
            if (value === 'tickets') {
                navigate('/dashboard/support?tab=tickets', { replace: true });
            } else if (articleSlug) {
                navigate(`/dashboard/support/${articleSlug}`, { replace: true });
            } else {
                navigate('/dashboard/support', { replace: true });
            }
        },
        [activeTab, navigate, articleSlug]
    );

    const handleTicketSelect = useCallback(
        (ticketId) => {
            const searchParams = new URLSearchParams(location.search || '');
            searchParams.set('tab', 'tickets');
            if (ticketId) {
                searchParams.set('ticket', ticketId);
            } else {
                searchParams.delete('ticket');
            }
            const query = searchParams.toString();
            navigate(`/dashboard/support${query ? `?${query}` : ''}`, { replace: true });
            setActiveTab('tickets');
            setInitialTicketId(ticketId || '');
        },
        [location.search, navigate]
    );

    const handleCategoryExplore = (category) => {
        if (!category) return;
        setExpandedCategory(category.id);
        const firstArticle = (category.articles || [])[0];
        if (firstArticle) {
            handleArticleSelect(firstArticle);
        }
    };

    const knowledgeBaseView = (
        <Box sx={{ width: '100%', px: { xs: 1, md: 3 }, pb: 3 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        md: '280px minmax(0, 1fr)',
                        lg: '300px minmax(0, 1fr)',
                    },
                    gap: 3,
                    alignItems: 'start',
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        width: '100%',
                        p: 2,
                        borderRadius: 3,
                        alignSelf: 'flex-start',
                        position: { md: 'sticky' },
                        top: { md: 24 },
                        maxHeight: { md: 'calc(100vh - 140px)' },
                        overflowY: { md: 'auto' },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <HelpOutlineIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                            Support Center
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton size="small" onClick={handleRefresh} disabled={refreshing || categoriesLoading}>
                            {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                    </Box>

                    <TextField
                        fullWidth
                        placeholder="Search help articles"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider sx={{ my: 2 }} />

                    {categoriesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : categoriesError ? (
                        <Typography color="error" variant="body2">
                            {categoriesError}
                        </Typography>
                    ) : searchTerm.trim() ? (
                        searchResults.length ? (
                            <List dense>
                                {searchResults.map((result) => (
                                    <ListItemButton
                                        key={`${result.slug}-search`}
                                        selected={result.slug === articleSlug}
                                        onClick={() => handleArticleSelect(result)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <ListItemText
                                            primary={result.title}
                                            secondary={`${result.category.name} • Updated ${formatDate(result.updatedAt)}`}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No articles matched your search.
                            </Typography>
                        )
                    ) : (
                        <Box>
                            {categories.map((category) => (
                                <Accordion
                                    key={category.id}
                                    disableGutters
                                    expanded={expandedCategory === category.id}
                                    onChange={(event, isExpanded) => {
                                        setExpandedCategory(isExpanded ? category.id : '');
                                    }}
                                    sx={{
                                        '&:before': { display: 'none' },
                                        boxShadow: 'none',
                                        border: '1px solid #e3f2fd',
                                        borderRadius: 2,
                                        mb: 1.5,
                                    }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Typography fontWeight={600}>{category.name}</Typography>
                                            {category.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {category.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0 }}>
                                        {category.articles.length ? (
                                            <List dense>
                                                {category.articles.map((articleSummary) => (
                                                    <ListItemButton
                                                        key={articleSummary.id}
                                                        onClick={() =>
                                                            handleArticleSelect({
                                                                ...articleSummary,
                                                                category: {
                                                                    id: category.id,
                                                                    name: category.name,
                                                                },
                                                            })
                                                        }
                                                        selected={articleSummary.slug === articleSlug}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        <ListItemText
                                                            primary={articleSummary.title}
                                                            secondary={
                                                                articleSummary.summary ||
                                                                `Updated ${formatDate(articleSummary.updatedAt)}`
                                                            }
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No articles yet in this category.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        minWidth: 0,
                        p: { xs: 2, md: 4 },
                        borderRadius: 3,
                        minHeight: 420,
                        border: '1px solid #e3f2fd',
                        backgroundColor: '#fff',
                    }}
                >
                    {articleLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : articleError ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" gutterBottom>
                                We couldn't load that article
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {articleError}
                            </Typography>
                        </Box>
                    ) : article ? (
                        <Box>
                            <Breadcrumbs sx={{ mb: 2 }}>
                                <MuiLink underline="hover" color="inherit" onClick={() => navigate('/dashboard/support')} sx={{ cursor: 'pointer' }}>
                                    Support
                                </MuiLink>
                                {article.category && (
                                    <MuiLink
                                        underline="hover"
                                        color="inherit"
                                        onClick={() => {
                                            setExpandedCategory(article.category.id);
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {article.category.name}
                                    </MuiLink>
                                )}
                                <Typography color="text.primary">{article.title}</Typography>
                            </Breadcrumbs>

                            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                                {article.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                                {article.category && <Chip label={article.category.name} color="primary" size="small" />}
                                <Typography variant="body2" color="text.secondary">
                                    Updated {formatDate(article.updatedAt)}
                                </Typography>
                            </Box>

                            {article.heroImageUrl && (
                                <Box sx={{ mb: 3 }}>
                                    <img
                                        src={resolveSupportAssetUrl(article.heroImageUrl)}
                                        alt="Article visual"
                                        style={{ width: '100%', borderRadius: 16, maxHeight: 360, objectFit: 'cover' }}
                                    />
                                </Box>
                            )}

                            <SupportArticleViewer content={article.content} />
                        </Box>
                    ) : (
                        <Box>
                            <Paper
                                sx={{
                                    p: { xs: 3, md: 5 },
                                    borderRadius: 4,
                                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    color: '#fff',
                                    mb: 4,
                                }}
                            >
                                <Typography variant="overline" sx={{ letterSpacing: 1.2 }}>
                                    Support Center
                                </Typography>
                                <Typography variant="h4" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
                                    How can we help you today?
                                </Typography>
                                <Typography variant="body1" sx={{ maxWidth: 540, opacity: 0.9 }}>
                                    Browse common topics, explore our feature guides, or jump into the latest updates curated by the support team.
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<SupportAgentIcon />}
                                        onClick={() => handleTabChange(null, 'tickets')}
                                    >
                                        Contact support
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        Browse articles
                                    </Button>
                                </Stack>
                            </Paper>

                            <Box sx={{ mb: 4 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                    <Box>
                                        <Typography variant="h5" fontWeight={700}>
                                            Browse by category
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Choose a category to dive into detailed walkthroughs and best practices.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    {categories.length ? (
                                        categories.map((category) => (
                                            <Grid item xs={12} sm={6} lg={4} key={category.id}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 2.5,
                                                        height: '100%',
                                                        borderRadius: 3,
                                                        border: '1px solid #e3f2fd',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {category.featuredImageUrl ? (
                                                        <Box sx={{ mx: -2.5, mt: -2.5, mb: 2, height: 140, overflow: 'hidden' }}>
                                                            <img
                                                                src={resolveSupportAssetUrl(category.featuredImageUrl)}
                                                                alt={`${category.name} visual`}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                mx: -2.5,
                                                                mt: -2.5,
                                                                mb: 2,
                                                                height: 140,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: '#e3f2fd',
                                                            }}
                                                        >
                                                            <HelpOutlineIcon color="primary" fontSize="large" />
                                                        </Box>
                                                    )}
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {category.name}
                                                        </Typography>
                                                        {category.description ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                {category.description}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                {category.articles.length} article{category.articles.length === 1 ? '' : 's'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1 }} />
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleCategoryExplore(category)}
                                                        disabled={!category.articles.length}
                                                    >
                                                        {category.articles.length ? 'View articles' : 'Coming soon'}
                                                    </Button>
                                                </Paper>
                                            </Grid>
                                        ))
                                    ) : (
                                        <Grid item xs={12}>
                                            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 3, border: '1px dashed #bbdefb' }}>
                                                <Typography variant="body1" color="text.secondary">
                                                    Categories will appear here once the support team has published them.
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>

                            <Box>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                                    <Typography variant="h5" fontWeight={700}>
                                        Latest articles
                                    </Typography>
                                </Stack>
                                {latestArticles.length ? (
                                    <Grid container spacing={2}>
                                        {latestArticles.map((articleSummary) => (
                                            <Grid item xs={12} md={4} key={articleSummary.id}>
                                                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0f2f1', height: '100%' }}>
                                                    {articleSummary.heroImageUrl && (
                                                        <Box sx={{ height: 160, overflow: 'hidden', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                                                            <img
                                                                src={resolveSupportAssetUrl(articleSummary.heroImageUrl)}
                                                                alt={articleSummary.title}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                            {articleSummary.category?.name && (
                                                                <Chip label={articleSummary.category.name} size="small" color="primary" />
                                                            )}
                                                            <Typography variant="caption" color="text.secondary">
                                                                Updated {formatDate(articleSummary.updatedAt)}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {articleSummary.title}
                                                        </Typography>
                                                        {articleSummary.summary && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                                            >
                                                                {articleSummary.summary}
                                                            </Typography>
                                                        )}
                                                    </CardContent>
                                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                                        <Button size="small" onClick={() => handleArticleSelect(articleSummary)}>
                                                            Read article
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 3, border: '1px dashed #c8e6c9' }}>
                                        <Typography variant="body1" color="text.secondary">
                                            New articles will be highlighted here as soon as they are published.
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, pb: 6 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <HelpOutlineIcon color="primary" />
                <Typography variant="h5" fontWeight={700}>
                    {activeTab === 'tickets' ? 'Support Tickets' : 'Support Center'}
                </Typography>
            </Stack>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ mb: 3 }}
                variant="scrollable"
                allowScrollButtonsMobile
            >
                <Tab label="Knowledge Base" value="knowledge" />
                <Tab label="My Tickets" value="tickets" />
            </Tabs>
            {activeTab === 'knowledge' ? (
                knowledgeBaseView
            ) : (
                <SupportTicketWorkspace
                    categories={categories}
                    initialTicketId={initialTicketId}
                    onTicketSelect={handleTicketSelect}
                />
            )}
        </Box>
    );
};

export default SupportCenter;
